// File: frontend/src/config/insuranceProducts.js

import { 
  Car, 
  Home, 
  PawPrint, 
  Plane, 
  Building2, 
  Heart, 
  Cross,
  FileText,
} from 'lucide-react';

import { ASSETS_BASE_URL } from './constants';

const S3_BASE = `${ASSETS_BASE_URL}/images/products`;

export const INSURANCE_PRODUCTS = {
  auto: {
    id: 'auto',
    slug: 'auto-insurance',
    name: 'Auto Insurance',
    icon: Car,
    color: '#2348ED',
    heroImage: `${S3_BASE}/auto-hero.jpg`,
    featureImage: `${S3_BASE}/auto-feature.jpg`,
    seoTitle: 'Auto Insurance Wallet | Manage Your Car Insurance - Allegory',
    seoDescription: 'Upload your auto insurance policy and let our AI analyze coverage, find gaps, and answer questions. Never miss a renewal or overpay again.',
    heroTitle: 'Your Auto Insurance, Finally Understood',
    heroSubtitle: 'Upload your car insurance policy and get instant insights on coverage, deductibles, and exclusions — in plain English.',
    whatItCovers: [
      'Liability coverage (bodily injury and property damage)',
      'Collision coverage (damage to your vehicle)',
      'Comprehensive coverage (theft, vandalism, weather)',
      'Uninsured/underinsured motorist protection',
      'Medical payments and personal injury protection',
      'Rental car reimbursement and roadside assistance'
    ],
    commonGaps: [
      'Insufficient liability limits for serious accidents',
      'Missing rental car coverage',
      'No gap insurance for financed vehicles',
      'Inadequate uninsured motorist protection'
    ],
    features: [
      {
        title: 'Coverage Analysis',
        description: 'Our AI reads your policy and identifies exactly what\'s covered, your deductibles, and any exclusions.'
      },
      {
        title: 'Gap Detection',
        description: 'We flag common coverage gaps like insufficient liability limits or missing rental car coverage.'
      },
      {
        title: 'Renewal Alerts',
        description: 'Get notified 90, 60, and 30 days before your policy expires so you can shop for better rates.'
      },
      {
        title: 'Claims Guidance',
        description: 'Step-by-step help filing claims, including what documents you need and what to expect.'
      }
    ]
  },

  home: {
    id: 'home',
    slug: 'home-insurance',
    name: 'Home Insurance',
    icon: Home,
    color: '#2348ED',
    heroImage: `${S3_BASE}/home-hero.jpg`,
    featureImage: `${S3_BASE}/home-feature.jpg`,
    seoTitle: 'Home Insurance Wallet | Manage Your Homeowners Insurance - Allegory',
    seoDescription: 'Upload your home insurance policy and understand your coverage limits, deductibles, and exclusions. Protect your biggest investment.',
    heroTitle: 'Know Exactly What Your Home Insurance Covers',
    heroSubtitle: 'Upload your homeowners policy and get instant clarity on dwelling coverage, personal property limits, and liability protection.',
    whatItCovers: [
      'Dwelling coverage (structure of your home)',
      'Personal property coverage (belongings)',
      'Liability protection (injuries on your property)',
      'Additional living expenses (temporary housing)',
      'Other structures (garage, shed, fence)',
      'Medical payments to others'
    ],
    commonGaps: [
      'Underinsured dwelling coverage (rebuild costs)',
      'Missing flood or earthquake coverage',
      'Insufficient personal property limits',
      'No sewer backup or water damage endorsement'
    ],
    features: [
      {
        title: 'Replacement Cost Analysis',
        description: 'We check if your dwelling coverage is enough to rebuild your home at current construction costs.'
      },
      {
        title: 'Endorsement Recommendations',
        description: 'Identify missing endorsements like flood, earthquake, or sewer backup coverage.'
      },
      {
        title: 'Inventory Management',
        description: 'Track high-value items and ensure they\'re properly covered under your policy.'
      },
      {
        title: 'Claims Assistance',
        description: 'Get guidance on filing claims for damage, theft, or liability incidents.'
      }
    ]
  },

  pet: {
    id: 'pet',
    slug: 'pet-insurance',
    name: 'Pet Insurance',
    icon: PawPrint,
    color: '#2348ED',
    heroImage: `${S3_BASE}/pet-hero.jpg`,
    featureImage: `${S3_BASE}/pet-feature.jpg`,
    seoTitle: 'Pet Insurance Wallet | Manage Your Pet Insurance - Allegory',
    seoDescription: 'Upload your pet insurance policy and understand what\'s covered, your deductibles, and reimbursement rates. Keep your furry friend protected.',
    heroTitle: 'Understand Your Pet Insurance Coverage',
    heroSubtitle: 'Upload your pet insurance policy and get clarity on what\'s covered, reimbursement rates, and exclusions.',
    whatItCovers: [
      'Accident coverage (injuries, broken bones)',
      'Illness coverage (infections, diseases)',
      'Hereditary and congenital conditions',
      'Prescription medications',
      'Emergency care and hospitalization',
      'Diagnostic tests and imaging'
    ],
    commonGaps: [
      'Pre-existing conditions excluded',
      'Wellness and preventive care not covered',
      'Breed-specific exclusions',
      'Low annual or lifetime limits'
    ],
    features: [
      {
        title: 'Coverage Breakdown',
        description: 'See exactly what accidents and illnesses are covered, and what\'s excluded.'
      },
      {
        title: 'Reimbursement Calculator',
        description: 'Understand your reimbursement rate and how much you\'ll pay out-of-pocket.'
      },
      {
        title: 'Claim Filing Help',
        description: 'Get step-by-step guidance on submitting claims and required documentation.'
      },
      {
        title: 'Renewal Reminders',
        description: 'Never miss a renewal and keep your pet continuously covered.'
      }
    ]
  },

  travel: {
    id: 'travel',
    slug: 'travel-insurance',
    name: 'Travel Insurance',
    icon: Plane,
    color: '#2348ED',
    heroImage: `${S3_BASE}/travel-hero.jpg`,
    featureImage: `${S3_BASE}/travel-feature.jpg`,
    seoTitle: 'Travel Insurance Wallet | Manage Your Travel Insurance - Allegory',
    seoDescription: 'Upload your travel insurance policy and understand trip cancellation coverage, medical protection, and baggage loss limits.',
    heroTitle: 'Travel with Confidence',
    heroSubtitle: 'Upload your travel insurance policy and know exactly what\'s covered if your trip gets cancelled or you need medical care abroad.',
    whatItCovers: [
      'Trip cancellation and interruption',
      'Emergency medical and dental care',
      'Medical evacuation and repatriation',
      'Baggage loss, damage, and delay',
      'Travel delays and missed connections',
      'Accidental death and dismemberment'
    ],
    commonGaps: [
      'Pre-existing medical conditions excluded',
      'Adventure activities not covered',
      'Insufficient medical evacuation limits',
      'No cancel-for-any-reason coverage'
    ],
    features: [
      {
        title: 'Trip Coverage Analysis',
        description: 'Understand what events qualify for trip cancellation and how much you\'ll be reimbursed.'
      },
      {
        title: 'Medical Coverage Abroad',
        description: 'See your medical coverage limits and whether you need supplemental insurance.'
      },
      {
        title: 'Claims Guidance',
        description: 'Get help filing claims for cancelled trips, medical emergencies, or lost baggage.'
      },
      {
        title: 'Policy Comparison',
        description: 'Compare your policy against industry standards and identify gaps.'
      }
    ]
  },

  life: {
    id: 'life',
    slug: 'life-insurance',
    name: 'Life Insurance',
    icon: Heart,
    color: '#2348ED',
    heroImage: `${S3_BASE}/life-hero.jpg`,
    featureImage: `${S3_BASE}/life-feature.jpg`,
    seoTitle: 'Life Insurance Wallet | Manage Your Life Insurance - Allegory',
    seoDescription: 'Upload your life insurance policy and understand your death benefit, beneficiaries, and policy terms. Protect your family\'s future.',
    heroTitle: 'Protect Your Family\'s Future',
    heroSubtitle: 'Upload your life insurance policy and get clarity on your death benefit, beneficiaries, and policy terms.',
    whatItCovers: [
      'Death benefit payout to beneficiaries',
      'Accidental death benefit (if included)',
      'Terminal illness rider (living benefit)',
      'Waiver of premium (if disabled)',
      'Cash value accumulation (whole/universal life)',
      'Policy loans and withdrawals'
    ],
    commonGaps: [
      'Insufficient coverage for family needs',
      'Outdated beneficiary designations',
      'Missing riders (disability, critical illness)',
      'No inflation protection'
    ],
    features: [
      {
        title: 'Coverage Adequacy Check',
        description: 'We analyze if your death benefit is sufficient for your family\'s needs.'
      },
      {
        title: 'Beneficiary Management',
        description: 'Track your beneficiaries and get reminders to update them after life events.'
      },
      {
        title: 'Policy Value Tracking',
        description: 'Monitor cash value growth and understand loan/withdrawal options.'
      },
      {
        title: 'Renewal & Premium Alerts',
        description: 'Get notified about premium due dates and policy renewal terms.'
      }
    ]
  },

  health: {
    id: 'health',
    slug: 'health-insurance',
    name: 'Health Insurance',
    icon: Cross,
    color: '#2348ED',
    heroImage: `${S3_BASE}/health-hero.jpg`,
    featureImage: `${S3_BASE}/health-feature.jpg`,
    seoTitle: 'Health Insurance Wallet | Manage Your Health Insurance - Allegory',
    seoDescription: 'Upload your health insurance policy and understand your deductibles, copays, and out-of-pocket maximums. Navigate healthcare costs with confidence.',
    heroTitle: 'Navigate Healthcare Costs with Confidence',
    heroSubtitle: 'Upload your health insurance policy and get instant clarity on deductibles, copays, and what\'s covered.',
    whatItCovers: [
      'Preventive care (annual checkups, screenings)',
      'Doctor visits and specialist consultations',
      'Hospital stays and surgeries',
      'Prescription drug coverage',
      'Mental health and substance abuse treatment',
      'Maternity and newborn care'
    ],
    commonGaps: [
      'High deductibles and out-of-pocket maximums',
      'Limited provider networks',
      'Prescription drug exclusions',
      'No dental or vision coverage'
    ],
    features: [
      {
        title: 'Cost Breakdown',
        description: 'Understand your deductible, copays, coinsurance, and out-of-pocket maximum.'
      },
      {
        title: 'Coverage Checker',
        description: 'Ask our AI if specific procedures, medications, or treatments are covered.'
      },
      {
        title: 'Claims Assistance',
        description: 'Get help understanding medical bills and filing claims or appeals.'
      },
      {
        title: 'Network Verification',
        description: 'Check if your doctors and hospitals are in-network before appointments.'
      }
    ]
  },

  commercial: {
    id: 'commercial',
    slug: 'commercial-insurance',
    name: 'Commercial Insurance',
    icon: Building2,
    color: '#2348ED',
    heroImage: `${S3_BASE}/commercial-hero.jpg`,
    featureImage: `${S3_BASE}/commercial-feature.jpg`,
    seoTitle: 'Commercial Insurance Wallet | Manage Your Business Insurance - Allegory',
    seoDescription: 'Upload your commercial insurance policies and understand your liability coverage, property protection, and business interruption limits.',
    heroTitle: 'Protect Your Business with Confidence',
    heroSubtitle: 'Upload your commercial insurance policies and get clarity on liability coverage, property protection, and business interruption.',
    whatItCovers: [
      'General liability (bodily injury, property damage)',
      'Commercial property (building, equipment)',
      'Business interruption (lost income)',
      'Professional liability (errors & omissions)',
      'Workers\' compensation',
      'Commercial auto (business vehicles)'
    ],
    commonGaps: [
      'Insufficient liability limits for lawsuits',
      'Missing cyber liability coverage',
      'No employment practices liability',
      'Inadequate business interruption coverage'
    ],
    features: [
      {
        title: 'Multi-Policy Management',
        description: 'Manage all your business insurance policies in one place.'
      },
      {
        title: 'Coverage Gap Analysis',
        description: 'Identify missing coverages like cyber liability or employment practices.'
      },
      {
        title: 'Claims Coordination',
        description: 'Get help filing claims across multiple policies and carriers.'
      },
      {
        title: 'Renewal Management',
        description: 'Track renewal dates for all policies and shop for better rates.'
      }
    ]
  },

  pension: {
    id: 'pension',
    slug: 'pension-insurance',
    name: 'Pension Insurance',
    icon: FileText,
    color: '#2348ED',
    heroImage: `${S3_BASE}/pension-hero.jpg`,
    featureImage: `${S3_BASE}/pension-feature.jpg`,
    seoTitle: 'Pension Insurance Wallet | Manage Your Pension - Allegory',
    seoDescription: 'Upload your pension documents and understand your retirement benefits, vesting schedule, and payout options.',
    heroTitle: 'Understand Your Retirement Benefits',
    heroSubtitle: 'Upload your pension documents and get clarity on your retirement benefits, vesting schedule, and payout options.',
    whatItCovers: [
      'Monthly retirement income',
      'Survivor benefits for spouse',
      'Disability benefits',
      'Early retirement options',
      'Cost-of-living adjustments',
      'Lump-sum payout options'
    ],
    commonGaps: [
      'Insufficient retirement income',
      'No inflation protection',
      'Limited survivor benefits',
      'Unclear vesting requirements'
    ],
    features: [
      {
        title: 'Benefit Calculator',
        description: 'Estimate your monthly pension income based on retirement age and years of service.'
      },
      {
        title: 'Vesting Tracker',
        description: 'Track your vesting schedule and know when you\'re fully vested.'
      },
      {
        title: 'Payout Options',
        description: 'Understand your payout options (monthly income vs. lump sum) and tax implications.'
      },
      {
        title: 'Survivor Benefits',
        description: 'See what benefits your spouse or dependents will receive after your death.'
      }
    ]
  }
};

// Helper functions remain the same
export const getProductBySlug = (slug) => {
  return Object.values(INSURANCE_PRODUCTS).find(product => product.slug === slug);
};

export const getAllProductSlugs = () => {
  return Object.values(INSURANCE_PRODUCTS).map(product => product.slug);
};

export const getProductById = (id) => {
  return INSURANCE_PRODUCTS[id];
};